import React, { useContext, useMemo } from 'react'
import styled from 'styled-components'

import {
  Announces,
  Authority,
  Configs,
  Docs,
  Form,
  Notes,
  Regularizations,
} from './Panes'
import FormalityProgressStatus from './FormalityProgressStatus'
import FormalityResume from './FormalityResume'
import FormalityAbortBtn from './FormalityAbortBtn'
import FormalityActions from './FormalityActions'
import FormalityRollbackBtn from './FormalityRollbackBtn'
import FormalityReloadDocumentsBtn from './FormalityReloadDocumentsBtn'
import FormalityActionsMenu from './FormalityActionsMenu'
import FormalityTags from './FormalityTags'

import {
  FormalityAbortWrapper,
  FormalityBox as Box,
  FormalityHeader,
} from './styles'
import { CurrentLegalFormalityContext } from 'contexts/LegalFormalityContext'
import { AbilityContext } from 'contexts/abilityContext'

type TabKey = 'Announces' | 'Configs' | 'Docs' | 'Form'

type PaneDef = {
  width?: string
  render: () => React.ReactNode
}

type TabDef = {
  key: TabKey
  label: string
  side?: 'left' | 'right'
  panes?: PaneDef[] // if present, renders a <Panes> grid
  render?: () => React.ReactNode // if present, renders custom content (no <Panes>)
}

const LegalFormality = () => {
  const { legalFormality, currentTab, setCurrentTab } = useContext(
    CurrentLegalFormalityContext
  )
  const ability: any = useContext(AbilityContext)
  const formalistOnly = ability.can('update', 'LegalFormality')

  const tabs: TabDef[] = useMemo(
    () => [
      {
        key: 'Form',
        label: 'Formulaire',
        panes: [
          { width: '50%', render: () => <Form /> },
          { render: () => <Notes /> },
        ],
      },
      {
        key: 'Docs',
        label: 'Documents',
        render: () => <Docs />,
      },
      ...(legalFormality.legalNoticeSupported
        ? [
            {
              key: 'Announces' as TabKey,
              label: 'Annonces Légales',
              render: () => <Announces />,
            },
          ]
        : []),
      // {
      //   key: 'Authority',
      //   label: 'Greffe',
      //   render: () => <Authority />,
      // },
      // {
      //   key: 'Regularizations',
      //   label: 'Rejets',
      //   render: () => <Regularizations />,
      // },
      //   RIGHT
      {
        key: 'Configs',
        label: 'Paramètres',
        render: () => <Configs />,
        side: 'right',
      },
    ],
    [legalFormality.legalNoticeSupported]
  )
  const leftTabs = tabs.filter(t => t.side !== 'right')
  const rightTabs = tabs.filter(t => t.side === 'right')

  const activeTab = tabs.find(t => t.key === currentTab) ?? tabs[0]

  return (
    <>
      <Box>
        <FormalityHeader>
          <FormalityResume />
          <FormalityProgressStatus />
        </FormalityHeader>
        {legalFormality.tags && legalFormality.tags.length > 0 && (
          <FormalityTags />
        )}
      </Box>

      <TabButtons>
        <SideTabs>
          {leftTabs.map(tab => (
            <TabButton
              key={tab.key}
              active={currentTab === tab.key}
              onClick={() => setCurrentTab(tab.key)}
            >
              {tab.label}
            </TabButton>
          ))}
        </SideTabs>

        <SideTabs>
          {rightTabs.map(tab => (
            <TabButton
              key={tab.key}
              active={currentTab === tab.key}
              onClick={() => setCurrentTab(tab.key)}
            >
              {tab.label}
            </TabButton>
          ))}
          <TabButton className={'float-right icon'} style={{ padding: '0' }}>
            <FormalityActionsMenu />
          </TabButton>
        </SideTabs>
      </TabButtons>

      <Box>
        {activeTab.panes ? (
          <Panes>
            {activeTab.panes.map((pane, idx) => (
              <Pane key={`${activeTab.key}-pane-${idx}`} width={pane.width}>
                {pane.render()}
              </Pane>
            ))}
          </Panes>
        ) : (
          activeTab.render?.()
        )}

        <FormalityActions />
      </Box>

      {formalistOnly && (
        <FormalityAbortWrapper>
          <FormalityReloadDocumentsBtn />
          <FormalityRollbackBtn />
          <FormalityAbortBtn />
        </FormalityAbortWrapper>
      )}
    </>
  )
}

const TabButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: space-between;
`

const TabButton = styled.a<{ active?: boolean; padding?: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;

  border-radius: 8px 8px 0 0;

  font-weight: 700;
  font-size: 20px;
  line-height: 1;

  background: ${props => (props.active ? '#ffffff' : '#f2f3f5')};
  color: ${props => (props.active ? '#171a1c' : '#92979c')};

  border: 1px solid ${props => (props.active ? '#e6e8eb' : '#e6e8eb')};
  border-bottom: none;
  box-shadow: ${props =>
    props.active ? 'rgba(0, 0, 0, 0.06) 3px -2px 3px 0px' : 'none'};
  cursor: pointer;
  text-decoration: none;

  padding: ${props => (props.padding ? props.padding : '10px 14px')};

  &:hover {
    background: ${props => (props.active ? '#ffffff' : '#eceef1')};
    color: #171a1c;
  }
`

const SideTabs = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const Panes = styled.div`
  display: flex;
  flex-flow: row wrap;
  gap: 16px;
  align-content: space-between;
  align-items: stretch;
`

const Pane = styled(Box)<{ width?: string }>`
  ${props => (props.width ? `width: ${props.width};` : '')}
  flex-shrink: 1;
  flex-grow: 1;
`

export default LegalFormality
